import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Globe, BookOpen, Headphones, Volume2, VolumeX, CheckCircle, 
  XCircle, ArrowLeft, ArrowRight, Play, Pause, Loader2, ChevronDown, ChevronUp, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ESOLLessonPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { topicId } = useParams();
  const { api, user } = useAuth();
  const audioRef = useRef(null);
  const isAdminPreview = location.pathname.includes('/admin/preview');
  
  const getBasePath = () => isAdminPreview ? '/admin/preview/esol' : '/student/esol';
  
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [currentSection, setCurrentSection] = useState('vocabulary');
  const [exerciseAnswers, setExerciseAnswers] = useState({});
  const [exerciseResults, setExerciseResults] = useState({});
  const [vocabExpanded, setVocabExpanded] = useState({});
  const [hasListenedAudio, setHasListenedAudio] = useState(false);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [wordAudioLoading, setWordAudioLoading] = useState(null); // Track which word is loading

  useEffect(() => {
    if (topicId) {
      fetchLesson();
    }
  }, [topicId]);

  // Track progress when user completes exercises
  useEffect(() => {
    if (!isAdminPreview && user?.id && lesson) {
      const correctCount = Object.values(exerciseResults).filter(r => r?.is_correct).length;
      const completedCount = Object.keys(exerciseResults).length;
      const vocabReviewed = Object.keys(vocabExpanded).filter(k => vocabExpanded[k]).length;
      
      // Auto-save progress
      if (completedCount > 0 || vocabReviewed > 0 || hasListenedAudio) {
        updateProgress(completedCount, correctCount, vocabReviewed);
      }
    }
  }, [exerciseResults, hasListenedAudio]);

  const updateProgress = async (exercisesCompleted, exercisesCorrect, vocabReviewed) => {
    if (isAdminPreview || !user?.id) return;
    
    try {
      await api.post(`/v2/esol/progress/${user.id}/${topicId}`, {
        exercises_completed: exercisesCompleted,
        exercises_correct: exercisesCorrect,
        vocabulary_reviewed: vocabReviewed,
        listened_audio: hasListenedAudio
      });
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const markLessonComplete = async () => {
    if (isAdminPreview || !user?.id) {
      toast.success('Lesson marked complete (preview mode)');
      setLessonCompleted(true);
      return;
    }
    
    try {
      await api.post(`/v2/esol/progress/${user.id}/${topicId}/complete-lesson`);
      setLessonCompleted(true);
      toast.success('Lesson completed! Great work! 🎉');
    } catch (error) {
      console.error('Failed to mark complete:', error);
      toast.error('Failed to save progress');
    }
  };

  const fetchLesson = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/v2/esol/lesson/${topicId}`);
      setLesson(response.data);
    } catch (error) {
      console.error('Error fetching lesson:', error);
      toast.error('Failed to load lesson');
      navigate(getBasePath());
    } finally {
      setLoading(false);
    }
  };

  const handlePlayAudio = async () => {
    if (audioRef.current && !audioLoading) {
      if (audioPlaying) {
        audioRef.current.pause();
        setAudioPlaying(false);
      } else {
        // If we already have audio loaded, just play it
        if (audioRef.current.src) {
          audioRef.current.play();
          setAudioPlaying(true);
        } else {
          // Generate audio
          await generateAudio();
        }
      }
    }
  };

  const generateAudio = async () => {
    if (!lesson?.listening_text) {
      toast.error('No audio content available for this lesson');
      return;
    }

    setAudioLoading(true);
    try {
      const response = await api.post(`/v2/esol/tts/lesson/${topicId}`, null, {
        params: { voice: 'fable' }  // British accent
      });
      
      if (response.data.audio) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio}`;
        audioRef.current.src = audioSrc;
        audioRef.current.play();
        setAudioPlaying(true);
        setHasListenedAudio(true);
        toast.success('Audio loaded!');
      }
    } catch (error) {
      console.error('Audio generation error:', error);
      if (error.response?.status === 402) {
        toast.error('Audio credits exceeded. Please add balance to your Universal Key.');
      } else {
        toast.error('Failed to generate audio');
      }
    } finally {
      setAudioLoading(false);
    }
  };

  const handleAnswerSelect = (exerciseIndex, answerIndex) => {
    setExerciseAnswers(prev => ({
      ...prev,
      [exerciseIndex]: answerIndex
    }));
  };

  const handleCheckAnswer = async (exerciseIndex) => {
    const answer = exerciseAnswers[exerciseIndex];
    if (answer === undefined) {
      toast.error('Please select an answer first');
      return;
    }

    try {
      const response = await api.post(`/v2/esol/exercise/${topicId}/check?exercise_index=${exerciseIndex}&answer=${answer}`);
      
      setExerciseResults(prev => ({
        ...prev,
        [exerciseIndex]: response.data
      }));

      if (response.data.is_correct) {
        toast.success('Correct!');
      } else {
        toast.error(`Incorrect. The answer is: ${response.data.correct_option}`);
      }
    } catch (error) {
      console.error('Error checking answer:', error);
      toast.error('Failed to check answer');
    }
  };

  const toggleVocabExpanded = (index) => {
    setVocabExpanded(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Play audio for individual word or phrase
  const playWordAudio = async (text, identifier) => {
    if (wordAudioLoading === identifier) return;
    
    setWordAudioLoading(identifier);
    try {
      const response = await api.post('/v2/esol/tts/word', { text });
      
      if (response.data.audio) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio}`;
        // Create a temporary audio element
        const tempAudio = new Audio(audioSrc);
        tempAudio.play();
        tempAudio.onended = () => setWordAudioLoading(null);
      }
    } catch (error) {
      console.error('Word audio error:', error);
      if (error.response?.status === 402) {
        toast.error('Audio credits exceeded. Please add balance.');
      } else {
        toast.error('Failed to play audio');
      }
      setWordAudioLoading(null);
    }
  };

  const levelColors = {
    'A1': 'bg-green-100 text-green-700 border-green-300',
    'A2': 'bg-blue-100 text-blue-700 border-blue-300',
    'B1': 'bg-purple-100 text-purple-700 border-purple-300',
    'B2': 'bg-orange-100 text-orange-700 border-orange-300'
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
        </div>
      </StudentLayout>
    );
  }

  if (!lesson) {
    return (
      <StudentLayout>
        <div className="text-center py-20">
          <p className="text-slate-600">Lesson not found</p>
          <Button onClick={() => navigate(getBasePath())} className="mt-4">
            Back to ESOL Lessons
          </Button>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div data-testid="esol-lesson-page" className="max-w-4xl mx-auto">
        {/* Hidden audio element */}
        <audio 
          ref={audioRef} 
          onEnded={() => setAudioPlaying(false)}
          onPause={() => setAudioPlaying(false)}
        />

        {/* Back Button */}
        <button 
          onClick={() => navigate(getBasePath())}
          className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to ESOL Lessons
        </button>

        {/* Header */}
        <div className="student-card bg-gradient-to-r from-blue-500 to-cyan-500 text-white mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${levelColors[lesson.level]} bg-opacity-90`}>
                    {lesson.level}
                  </span>
                </div>
                <h1 className="font-heading text-2xl font-bold">{lesson.topic}</h1>
                <p className="text-blue-100 text-sm">{lesson.description}</p>
              </div>
            </div>
            
            {/* Audio Button */}
            {lesson.has_audio && (
              <Button
                onClick={handlePlayAudio}
                disabled={audioLoading}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
                data-testid="listen-button"
              >
                {audioLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : audioPlaying ? (
                  <Pause className="w-5 h-5 mr-2" />
                ) : (
                  <Headphones className="w-5 h-5 mr-2" />
                )}
                {audioLoading ? 'Loading...' : audioPlaying ? 'Pause' : 'Listen'}
              </Button>
            )}
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['vocabulary', 'phrases', 'exercises', 'listening'].map((section) => (
            <button
              key={section}
              onClick={() => setCurrentSection(section)}
              className={`px-4 py-2 rounded-full font-semibold whitespace-nowrap transition-all ${
                currentSection === section
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`tab-${section}`}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
              {section === 'vocabulary' && ` (${lesson.vocabulary?.length || 0})`}
              {section === 'exercises' && ` (${lesson.exercises?.length || 0})`}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        <div className="space-y-4">
          {/* Vocabulary Section */}
          {currentSection === 'vocabulary' && (
            <div className="student-card" data-testid="vocabulary-section">
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-600" />
                Vocabulary
              </h2>
              <div className="grid gap-3">
                {lesson.vocabulary?.map((item, index) => (
                  <div
                    key={index}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                    data-testid={`vocab-item-${index}`}
                  >
                    <div className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <button
                        onClick={() => toggleVocabExpanded(index)}
                        className="flex-1 flex items-center gap-3 text-left"
                      >
                        <span className="text-lg font-bold text-slate-800">{item.word}</span>
                        <span className="text-sm text-slate-500 font-mono">{item.phonetic}</span>
                        {vocabExpanded[index] ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      {/* Listen button for individual word */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          playWordAudio(item.word, `vocab-${index}`);
                        }}
                        disabled={wordAudioLoading === `vocab-${index}`}
                        className="ml-2 p-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 transition-colors disabled:opacity-50"
                        data-testid={`listen-vocab-${index}`}
                        title={`Listen to "${item.word}"`}
                      >
                        {wordAudioLoading === `vocab-${index}` ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Volume2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {vocabExpanded[index] && (
                      <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50">
                        <p className="text-slate-600 italic">"{item.example}"</p>
                        {/* Listen button for example sentence */}
                        <button
                          onClick={() => playWordAudio(item.example, `vocab-example-${index}`)}
                          disabled={wordAudioLoading === `vocab-example-${index}`}
                          className="mt-2 flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700"
                          data-testid={`listen-vocab-example-${index}`}
                        >
                          {wordAudioLoading === `vocab-example-${index}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Volume2 className="w-4 h-4" />
                          )}
                          Listen to example
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phrases Section */}
          {currentSection === 'phrases' && (
            <div className="student-card" data-testid="phrases-section">
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-blue-600" />
                Useful Phrases
              </h2>
              <div className="space-y-3">
                {lesson.phrases?.map((phrase, index) => (
                  <div
                    key={index}
                    className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-between"
                    data-testid={`phrase-${index}`}
                  >
                    <p className="text-slate-800 font-medium text-lg flex-1">"{phrase}"</p>
                    {/* Listen button for phrase */}
                    <button
                      onClick={() => playWordAudio(phrase, `phrase-${index}`)}
                      disabled={wordAudioLoading === `phrase-${index}`}
                      className="ml-3 p-2 rounded-full bg-blue-200 hover:bg-blue-300 text-blue-700 transition-colors disabled:opacity-50"
                      data-testid={`listen-phrase-${index}`}
                      title="Listen to this phrase"
                    >
                      {wordAudioLoading === `phrase-${index}` ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Volume2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Exercises Section */}
          {currentSection === 'exercises' && (
            <div className="student-card" data-testid="exercises-section">
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Practice Exercises
              </h2>
              <div className="space-y-6">
                {lesson.exercises?.map((exercise, exerciseIndex) => {
                  const result = exerciseResults[exerciseIndex];
                  const selectedAnswer = exerciseAnswers[exerciseIndex];
                  
                  return (
                    <div
                      key={exerciseIndex}
                      className={`p-5 rounded-xl border-2 ${
                        result?.is_correct 
                          ? 'border-green-300 bg-green-50' 
                          : result?.is_correct === false 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-slate-200 bg-white'
                      }`}
                      data-testid={`exercise-${exerciseIndex}`}
                    >
                      <p className="font-semibold text-slate-800 mb-4">
                        {exerciseIndex + 1}. {exercise.question}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                        {exercise.options.map((option, optionIndex) => {
                          const isSelected = selectedAnswer === optionIndex;
                          const isCorrect = result && result.correct_answer === optionIndex;
                          const isWrong = result && isSelected && !result.is_correct;
                          
                          return (
                            <button
                              key={optionIndex}
                              onClick={() => !result && handleAnswerSelect(exerciseIndex, optionIndex)}
                              disabled={!!result}
                              className={`p-3 rounded-lg text-left transition-all ${
                                isCorrect
                                  ? 'bg-green-500 text-white'
                                  : isWrong
                                    ? 'bg-red-500 text-white'
                                    : isSelected
                                      ? 'bg-purple-500 text-white'
                                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                              } ${result ? 'cursor-default' : 'cursor-pointer'}`}
                              data-testid={`option-${exerciseIndex}-${optionIndex}`}
                            >
                              <span className="font-medium">{String.fromCharCode(65 + optionIndex)}.</span> {option}
                              {isCorrect && <CheckCircle className="w-4 h-4 inline ml-2" />}
                              {isWrong && <XCircle className="w-4 h-4 inline ml-2" />}
                            </button>
                          );
                        })}
                      </div>
                      {!result && (
                        <Button
                          onClick={() => handleCheckAnswer(exerciseIndex)}
                          disabled={selectedAnswer === undefined}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid={`check-answer-${exerciseIndex}`}
                        >
                          Check Answer
                        </Button>
                      )}
                      {result && (
                        <div className={`mt-2 p-3 rounded-lg ${result.is_correct ? 'bg-green-100' : 'bg-red-100'}`}>
                          <p className={`font-medium ${result.is_correct ? 'text-green-800' : 'text-red-800'}`}>
                            {result.is_correct ? 'Correct!' : `The correct answer is: ${result.correct_option}`}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Listening Section */}
          {currentSection === 'listening' && (
            <div className="student-card" data-testid="listening-section">
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Headphones className="w-5 h-5 text-cyan-600" />
                Listening Practice
              </h2>
              {lesson.listening_text ? (
                <div className="space-y-4">
                  <div className="p-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl border border-cyan-100">
                    <p className="text-slate-700 text-lg leading-relaxed whitespace-pre-wrap">
                      {lesson.listening_text}
                    </p>
                  </div>
                  <Button
                    onClick={handlePlayAudio}
                    disabled={audioLoading}
                    size="lg"
                    className="bg-cyan-600 hover:bg-cyan-700 w-full"
                    data-testid="play-audio-button"
                  >
                    {audioLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : audioPlaying ? (
                      <Pause className="w-5 h-5 mr-2" />
                    ) : (
                      <Play className="w-5 h-5 mr-2" />
                    )}
                    {audioLoading ? 'Generating Audio...' : audioPlaying ? 'Pause Audio' : 'Listen to This Text'}
                  </Button>
                  <p className="text-sm text-slate-500 text-center">
                    Click to hear this text read aloud by AI. Great for pronunciation practice!
                  </p>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  No listening content available for this lesson.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Navigation & Complete Button */}
        <div className="flex justify-between items-center mt-8">
          <Button
            variant="outline"
            onClick={() => navigate(getBasePath())}
            data-testid="nav-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Lessons
          </Button>
          
          {!lessonCompleted ? (
            <Button
              onClick={markLessonComplete}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="complete-lesson-btn"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Mark as Complete
            </Button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg font-medium">
              <CheckCircle className="w-5 h-5" />
              Lesson Complete!
            </div>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default ESOLLessonPage;
